import { useMemo } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { rankingsHubQueryOptions } from '../hooks/use-rankings-hub'
import { Card, Chip, Surface, Tabs } from '@heroui/react'
import { useNavigate } from '@tanstack/react-router'
import { useLocale, useTranslations } from 'use-intl'
import { ChevronRight, MapPin, Utensils } from 'lucide-react'
import { Route } from '~/routes/{-$locale}/_public/rankings/index'
import { LocalizedLink } from '~/components/common/localized-link'

export function RankingsHubPage() {
  const { year } = Route.useSearch()
  const locale = useLocale()
  const t = useTranslations('rankings')
  const navigate = useNavigate({ from: Route.fullPath })
  const { data } = useSuspenseQuery(rankingsHubQueryOptions(year, locale))

  const availableYears = [2026, 2025, 2024, 2023, 2022, 2021, 2020]
  const categories = useMemo(() => {
    const grouped = new Map<string, {
      id: number
      sourceId: string
      name: string
      href: string
      totalShops: number
      regions: { id: number, sourceId: string, name: string, href: string, snapshotId: string, totalShops: number }[]
    }>()

    for (const category of (data.categories || []) as any[]) {
      const key = category.name
      if (!grouped.has(key)) {
        grouped.set(key, {
          id: category.id,
          sourceId: category.sourceId,
          name: category.name,
          href: category.href,
          totalShops: 0,
          regions: [],
        })
      }

      const entry = grouped.get(key)!
      const categoryTotal = (category.regions || []).reduce((sum: number, region: any) => sum + (region.totalShops || 0), 0)
      if (categoryTotal > entry.totalShops) {
        entry.id = category.id
        entry.sourceId = category.sourceId
        entry.href = category.href
      }
      for (const region of category.regions || []) {
        if (!region.totalShops || region.totalShops <= 0)
          continue
        if (entry.regions.some(item => item.snapshotId === region.snapshotId))
          continue

        const totalShops = region.totalShops
        entry.totalShops += totalShops
        entry.regions.push({
          id: region.id,
          sourceId: region.sourceId,
          name: region.name,
          href: region.href || category.href,
          snapshotId: region.snapshotId,
          totalShops,
        })
      }
    }

    return Array.from(grouped.values())
      .filter(category => category.totalShops > 0 && category.regions.length > 0)
      .map(category => ({
        ...category,
        regions: category.regions.sort((a, b) => b.totalShops - a.totalShops || a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => b.totalShops - a.totalShops || a.name.localeCompare(b.name))
  }, [data.categories])
  const totalCategoryShops = categories.reduce((sum, category) => sum + category.totalShops, 0)
  const totalCategoryRankings = categories.reduce((sum, category) => sum + category.regions.length, 0)
  const hasCategories = categories.length > 0

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8 md:py-10">
      <Surface className="relative mb-8 min-h-[320px] overflow-hidden rounded-3xl p-6 shadow-sm md:p-10" variant="tertiary">
        <img
          src="/images/guides/categories/japanese-cuisine/hero.png"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/10" />
        <div className="relative z-10 flex min-h-[260px] max-w-3xl flex-col justify-between gap-8 text-white">
          <div>
            <Chip color="warning" variant="soft" className="mb-5 border-white/20 bg-white/15 text-white backdrop-blur">
              <Utensils className="size-3.5" />
              <Chip.Label>{t('eyebrow', { year })}</Chip.Label>
            </Chip>
            <h1 className="max-w-2xl text-4xl font-black leading-tight tracking-tight md:text-5xl">
              {t('heroTitle')}
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-white/82 md:text-lg">
              {t('heroSubtitle')}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Chip size="lg" variant="soft" className="bg-white/14 text-white backdrop-blur">
              <Utensils className="size-4" />
              <Chip.Label>{totalCategoryShops} {t('statsMeitenShops')}</Chip.Label>
            </Chip>
            <Chip size="lg" variant="soft" className="bg-white/14 text-white backdrop-blur">
              <Utensils className="size-4" />
              <Chip.Label>{categories.length} {t('statsCategories')}</Chip.Label>
            </Chip>
            <Chip size="lg" variant="soft" className="bg-white/14 text-white backdrop-blur">
              <MapPin className="size-4" />
              <Chip.Label>{totalCategoryRankings} {t('statsLists')}</Chip.Label>
            </Chip>
          </div>
        </div>
      </Surface>

      <Tabs
        className="mb-10 w-full"
        selectedKey={String(year)}
        onSelectionChange={key => navigate({ search: { year: Number(key) } })}
      >
        <Tabs.ListContainer className="overflow-x-auto">
          <Tabs.List aria-label={t('yearTabsLabel')} className="w-max min-w-full">
            {availableYears.map((y, index) => (
              <Tabs.Tab key={y} id={String(y)} className="min-h-11 px-5">
                {index > 0 && <Tabs.Separator />}
                {y}
                <Tabs.Indicator />
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs.ListContainer>
      </Tabs>

      <div className="space-y-14">
        {hasCategories && (
          <section>
            <div className="mb-5">
              <div className="mb-2 flex items-center gap-2 text-primary">
                <Utensils className="size-5" />
                <span className="text-sm font-semibold">{t('meitenLabel')}</span>
              </div>
              <h2 className="text-2xl font-black tracking-tight md:text-3xl">{t('categoriesSection', { year })}</h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-secondary dark:text-foreground/70">{t('categoriesDescription')}</p>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {categories.map(category => (
                <Card key={`${category.id}-${category.name}`} className="h-full overflow-hidden p-5 shadow-sm transition-shadow hover:shadow-md" variant="default">
                  <div className="absolute inset-x-0 top-0 h-1 bg-primary" />
                  <Card.Header className="mb-4 flex-row items-start justify-between gap-3 p-0">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                        <Utensils className="size-5" />
                      </span>
                      <div className="min-w-0">
                        <LocalizedLink
                          to={category.href}
                          className="cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                          <Card.Title className="text-xl font-black transition-colors hover:text-primary">
                            {category.name}
                          </Card.Title>
                        </LocalizedLink>
                        <Card.Description className="mt-1">
                          {t('categorySummary', { count: category.regions.length, entries: category.totalShops })}
                        </Card.Description>
                      </div>
                    </div>
                    <Chip size="sm" variant="soft" color="accent">
                      <Chip.Label>{year}</Chip.Label>
                    </Chip>
                  </Card.Header>
                  <Card.Content className="gap-2 p-0">
                    {category.regions.map(region => (
                      <LocalizedLink
                        key={region.snapshotId}
                        to={region.href || category.href}
                        search={{ region: region.sourceId, page: 1 }}
                        className="group block cursor-pointer rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <div className="flex min-h-12 items-center justify-between gap-3 rounded-xl bg-surface-container-lowest px-3 py-2 transition-colors group-hover:bg-surface-container-low">
                          <span className="flex min-w-0 items-center gap-2 font-semibold">
                            <MapPin className="size-4 shrink-0 text-primary/70" />
                            <span className="truncate">{region.name}</span>
                          </span>
                          <span className="flex items-center gap-2 text-sm text-secondary dark:text-foreground/70">
                            <Chip size="sm" variant="soft">
                              <Chip.Label>{t('entriesShort', { count: region.totalShops })}</Chip.Label>
                            </Chip>
                            <ChevronRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                          </span>
                        </div>
                      </LocalizedLink>
                    ))}
                  </Card.Content>
                </Card>
              ))}
            </div>
          </section>
        )}

        {!hasCategories && (
          <Surface className="py-20 text-center text-secondary" variant="secondary">
            {t('noData', { year })}
          </Surface>
        )}
      </div>
    </div>
  )
}
